import { combineReducers } from "redux";

import affiliateBuilder, { AffiliateBuilderState } from "./affiliateBuilder";
import affiliates, { AffiliateResourcesState } from "./affiliates";
import affiliateImages, { AffiliateImageState } from "./affiliatesImages";
import analyzerTree, { AnalyzerTreeState } from "./analyzerTree";
import applicationAgents, { ApplicationAgentsState } from "./applicationAgents";
import applicationAlerts, { ApplicationAlertsState } from "./applicationAlerts";
import applicationBuilder, { ApplicationBuilderState } from "./applicationBuilder";
import applicationCommandCenter, { ApplicationCommandCenterResourcesState } from "./applicationCommand";
import applicationComments, { ApplicationCommentsState } from "./applicationComments";
import applicationGmeGsi, { ApplicationGmeGsiState } from "./applicationGmeGsi";
import applicationInsureds, { ApplicationInsuredsState } from "./applicationInsureds";
import applicationLtc, { ApplicationLtcState } from "./applicationLtc";
import applicationMemos, { ApplicationMemosState } from "./applicationMemos";
import applicationPremium, { ApplicationPremiumDetailsState } from "./applicationPremium";
import applicationProduct, { ApplicationProductState } from "./applicationProduct";
import applicationRequirements, { ApplicationRequirementsState } from "./applicationRequirements";
import applications, { ApplicationResourcesState } from "./applications";
import auth, { AuthState } from "./authentication";
import brokerAnalyzerPreferences, { IBrokerAnalyzerPreferencesState } from "./brokerAnalyzerPreferences";
import brokerBuilder, { BrokerBuilderState } from "./brokerBuilder";
import brokerComments, { BrokerCommentsState } from "./brokerComments";
import brokerCommissions, { BrokerCommissionsState } from "./brokerCommissions";
import brokerContracts, { BrokerContractsState } from "./brokerContracts";
import brokerDocuments, { BrokerDocumentsState } from "./brokerDocuments";
import brokers, { BrokerResourcesState } from "./brokers";
import brokerSecureUpload, { BrokerSecureUploadState } from "./brokerSecureUpload";
import brokerImages, { BrokerImageState } from "./brokersImages";
import brokerTasks, { BrokerTasksState } from "./brokerTasks";
import campaign, { CampaignResourcesState } from "./campaign";
import dncEmail, { DncEmailResourcesState } from "./campaignDncEmail";
import dncPhone, { DncPhoneResourcesState } from "./campaignDncPhone";
import companies, { CompanyResourcesState } from "./companies";
import companyBuilder, { CompanyBuilderState } from "./companyBuilder";
import correspondence, { CorrespondenceState } from "./correspondence";
import documentation, { DocumentationState } from "./documentation";
import healthCheck, { HealthCheckState } from "./healthCheck";
import preproposals, { PreproposalsResourcesState } from "./preproposals";
import productBuilder, { ProductBuilderState } from "./productBuilder";
import products, { ProductResourcesState } from "./products";
import productsV2, { ProductV2ResourcesState } from "./productsV2";
import proposalCensus, { ProposalCensusState } from "./proposalCensus";
import proposalDisclaimer, { IProposalDisclaimerState } from "./proposalDisclaimer";
import proposalIndividualCarier, { IProposalIndividualCarierState } from "./proposalIndividualCarier";
import proposalNotes, { ProposalNotesState } from "./proposalNotes";
import proposalOptions, { ProposalOptionsState } from "./proposalOptions";
import proposalRevisions, { ProposalRevisionsState } from "./proposalRevisions";
import proposals, { ProposalResourcesState } from "./proposals";
import prospects, { ProspectResourcesState } from "./prospects";
import prospectsBuilder, { ProspectsBuilderState } from "./prospectsBuilder";
import questionAnswersStatus, { IQuestionAnswersStatusState } from "./question-answers-status";
import reports, { ReportResourcesState } from "./reports";
import { ISecureUploadState, SecureUploadState } from "./secureUpload";
import tasksCommand, { TasksCommandCenterResourcesState } from "./tasksCommand";
import templateBuilder, { TemplateBuilderState } from "./templateBuilder";
import templates, { TemplateResourcesState } from "./templates";
import users, { UsersState } from "./usersBuilder";

export interface IRootState {
  readonly auth: AuthState;
  readonly applications: ApplicationResourcesState;
  readonly applicationBuilder: ApplicationBuilderState;
  readonly applicationComments: ApplicationCommentsState;
  readonly applicationAlerts: ApplicationAlertsState;
  readonly applicationLtc: ApplicationLtcState;
  readonly applicationPremium: ApplicationPremiumDetailsState;
  readonly applicationAgents: ApplicationAgentsState;
  readonly applicationRequirements: ApplicationRequirementsState;
  readonly applicationInsureds: ApplicationInsuredsState;
  readonly applicationMemos: ApplicationMemosState;
  readonly applicationProduct: ApplicationProductState;
  readonly applicationGmeGsi: ApplicationGmeGsiState;
  readonly applicationCommandCenter: ApplicationCommandCenterResourcesState;
  readonly tasksCommand: TasksCommandCenterResourcesState;
  readonly proposals: ProposalResourcesState;
  readonly proposalOptions: ProposalOptionsState;
  readonly productBuilder: ProductBuilderState;
  readonly brokers: BrokerResourcesState;
  readonly brokerImages: BrokerImageState;
  readonly brokerComments: BrokerCommentsState;
  readonly companies: CompanyResourcesState;
  readonly affiliates: AffiliateResourcesState;
  readonly affiliateImages: AffiliateImageState;
  readonly products: ProductResourcesState;
  readonly productsV2: ProductV2ResourcesState;
  readonly companyBuilder: CompanyBuilderState;
  readonly affiliateBuilder: AffiliateBuilderState;
  readonly brokerBuilder: BrokerBuilderState;
  readonly analyzerTree: AnalyzerTreeState;
  readonly documentation: DocumentationState;
  readonly correspondence: CorrespondenceState;
  readonly proposalNotes: ProposalNotesState;
  readonly proposalIndividualCarier: IProposalIndividualCarierState;
  readonly proposalCensus: ProposalCensusState;
  readonly proposalRevisions: ProposalRevisionsState;
  readonly proposalDisclaimer: IProposalDisclaimerState;
  readonly questionAnswersStatus: IQuestionAnswersStatusState;
  readonly SecureUploadState: ISecureUploadState;
  readonly brokerSecureUpload: BrokerSecureUploadState;
  readonly brokerDocuments: BrokerDocumentsState;
  readonly brokerContracts: BrokerContractsState;
  readonly brokerTasks: BrokerTasksState;
  readonly brokerCommissions: BrokerCommissionsState;
  readonly brokerAnalyzerPreferences: IBrokerAnalyzerPreferencesState;
  readonly healthCheck: HealthCheckState;
  readonly templates: TemplateResourcesState;
  readonly templateBuilder: TemplateBuilderState;
  readonly dncEmail: DncEmailResourcesState;
  readonly dncPhone: DncPhoneResourcesState;
  readonly campaign: CampaignResourcesState;
  readonly preproposals: PreproposalsResourcesState;
  readonly users: UsersState;
  readonly reports: ReportResourcesState;
  readonly prospects: ProspectResourcesState;
  readonly prospectsBuilder: ProspectsBuilderState;
}

const rootReducer = combineReducers<IRootState>({
  auth,
  proposals,
  applications,
  applicationBuilder,
  applicationAgents,
  applicationComments,
  applicationAlerts,
  applicationLtc,
  applicationPremium,
  applicationRequirements,
  applicationInsureds,
  applicationMemos,
  applicationProduct,
  applicationGmeGsi,
  applicationCommandCenter,
  tasksCommand,
  proposalOptions,
  productBuilder,
  brokers,
  brokerImages,
  brokerComments,
  brokerAnalyzerPreferences,
  companies,
  affiliates,
  affiliateImages,
  products,
  productsV2,
  companyBuilder,
  affiliateBuilder,
  brokerBuilder,
  analyzerTree,
  documentation,
  correspondence,
  proposalNotes,
  proposalCensus,
  proposalRevisions,
  proposalDisclaimer,
  questionAnswersStatus,
  SecureUploadState,
  brokerSecureUpload,
  proposalIndividualCarier,
  healthCheck,
  templates,
  templateBuilder,
  dncEmail,
  dncPhone,
  campaign,
  preproposals,
  users,
  brokerDocuments,
  brokerContracts,
  brokerTasks,
  brokerCommissions,
  reports,
  prospects,
  prospectsBuilder,
});

export default rootReducer;
